"use client";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import TopNavThree from "../../components/Header/TopNav/TopNavThree";
import MenuFurniture from "../../components/Header/Menu/MenuFurniture";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import Footer from "../../components/Footer/Footer";
import { ProductType } from "../../type/ProductType";
import Product from "../../components/Product/Product";
import HandlePagination from "../../components/Other/HandlePagination";
import { fetchProductImages, fetchProductText } from "../../data/Productlist";
import { tiktokSearch } from "../../utils/tiktokPixel";

const SearchResult = () => {
  const [dataSourceProd, setDataSourceProd] = useState<any[]>([]);
  const [isOpenProduct, setisOpenProduct] = useState(true);
  const [isLoadingText, setIsLoadingText] = useState(true);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const productsPerPage = 8;

  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";

  const handleSearch = (value: string) => {
    if (value.trim()) {
      router(`/search-result?query=${encodeURIComponent(value.trim())}`);
      setSearchKeyword("");
    }
  };

  const fetchData = async () => {
    try {
      // Fetch text data first
      const products = await fetchProductText();

      // Set text data first (without images)
      setDataSourceProd(products);
      setisOpenProduct(false);
      setIsLoadingText(false);

      // Fetch images for the products after the text data is loaded
      const productsWithImages = await fetchProductImages(products);

      // Set the updated products with images
      setDataSourceProd(productsWithImages);

      setIsLoadingImages(false);
    } catch (error) {
      console.error("Failed to fetch products: ", error);
      setisOpenProduct(false);
      setIsLoadingText(false);
      setIsLoadingImages(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Reset page when query changes
  useEffect(() => {
    setCurrentPage(0);
  }, [query]);

  // Memoize filtered data to prevent infinite re-renders
  const filteredData = useMemo(() => {
    if (query && dataSourceProd.length > 0) {
      return dataSourceProd.filter((product) => {
        const searchTerm = query.toLowerCase().trim();
        const productName = (product.name || "").toLowerCase();
        const productCategory = (product.category || "").toLowerCase();
        const productDescription = (product.description || "").toLowerCase();
        const productBrand = (product.brand || "").toLowerCase();
        
        return (
          productName.includes(searchTerm) ||
          productCategory.includes(searchTerm) ||
          productDescription.includes(searchTerm) ||
          productBrand.includes(searchTerm)
        );
      });
    } else if (query) {
      // Show loading state while data is being fetched
      return [];
    } else {
      // If no query, show all products
      return dataSourceProd;
    }
  }, [query, dataSourceProd]);

  const searchTrackedRef = useRef("");
  useEffect(() => {
    const q = query.trim();
    if (!q || searchTrackedRef.current === q) return;
    searchTrackedRef.current = q;
    tiktokSearch({
      searchString: q,
      contents: filteredData.slice(0, 10).map((product) => ({
        content_id: String(product.id),
        content_name: String(product.name || ""),
        content_type: "product",
      })),
    });
  }, [query, filteredData]);

  // Memoize the no-data fallback
  const finalFilteredData = useMemo(() => {
    if (filteredData.length === 0 && query && !isLoadingText && !isLoadingImages) {
      return [
        {
          id: "no-data",
          category: "no-data",
          type: "no-data",
          name: "no-data",
          gender: "no-data",
          new: false,
          sale: false,
          rate: 0,
          price: 0,
          originPrice: 0,
          brand: "no-data",
          sold: 0,
          quantity: 0,
          quantityPurchase: 0,
          sizes: [],
          variation: [],
          thumbImage: [],
          images: [],
          description: "no-data",
          action: "no-data",
          slug: "no-data",
        },
      ];
    }
    return filteredData;
  }, [filteredData, query, isLoadingText, isLoadingImages]);

  // Memoize pagination calculations
  const pageCount = useMemo(() => {
    return Math.ceil(finalFilteredData.length / productsPerPage);
  }, [finalFilteredData]);

  // Memoize current products
  const currentProducts = useMemo(() => {
    const offset = currentPage * productsPerPage;
    if (finalFilteredData.length > 0) {
      return finalFilteredData.slice(offset, offset + productsPerPage);
    } else {
      return [];
    }
  }, [finalFilteredData, currentPage, productsPerPage]);

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
  };

  return (
    <>
      <TopNavThree props="style-three bg-white" />
      <div id="header" className="relative w-full">
        <MenuFurniture props="" />
        <Breadcrumb
          heading="résultat de recherche"
          subHeading="résultat de recherche"
        />
      </div>
      <div className="shop-product breadcrumb1 py-10">
        <div className="container">
          <div className="heading flex flex-col items-center">
            {isLoadingText || isLoadingImages ? (
              <div className="heading4 text-center">
                Chargement des résultats de recherche...
              </div>
            ) : (
              <div className="heading4 text-center">
                {query ? (
                  <>
                    Trouvé {finalFilteredData.length} résultat{finalFilteredData.length !== 1 ? 's' : ''} pour {String.raw`"`}
                    {query}
                    {String.raw`"`}.
                  </>
                ) : (
                  "Tous les produits"
                )}
              </div>
            )}
            <div className="input-block lg:w-1/2 sm:w-3/5 w-full md:h-[52px] h-[44px] sm:mt-8 mt-5">
              <div className="w-full h-full relative">
                <input
                  type="text"
                  placeholder="Recherche..."
                  className="caption1 w-full h-full pl-4 md:pr-[150px] pr-32 rounded-xl border border-line"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleSearch(searchKeyword)
                  }
                />
                <button
                  className="button-main absolute top-1 bottom-1 right-1 flex items-center justify-center !bg-pink"
                  onClick={() => handleSearch(searchKeyword)}
                >
                  rechercher
                </button>
              </div>
            </div>
          </div>
          <div className="list-product-block relative md:pt-10 pt-6">
            {isLoadingText || isLoadingImages ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink"></div>
              </div>
            ) : (
              <>
                {query && (
                  <div className="heading6 mb-5">Recherche de produit : {query}</div>
                )}
                <div
                  className={`list-product hide-product-sold grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-2 sm:gap-[30px] gap-[20px] mt-5`}
                >
                  {currentProducts.map((item) =>
                    item.id === "no-data" ? (
                      <div key={item.id} className="col-span-full text-center py-20">
                        <div className="text-xl font-semibold text-gray-600 mb-4">
                          Aucun produit ne correspond à votre recherche
                        </div>
                        <div className="text-gray-500 mb-6">
                          Essayez avec des mots-clés différents ou parcourez nos catégories
                        </div>
                        <a
                          href="/shop"
                          className="button-main bg-pink hover:bg-greenprimary text-white px-6 py-3 rounded-lg"
                        >
                          Voir tous les produits
                        </a>
                      </div>
                    ) : (
                      <Product key={item.id} data={item} type="grid" />
                    )
                  )}
                </div>

                {pageCount > 1 && (
                  <div className="list-pagination flex items-center justify-center md:mt-10 mt-7">
                    <HandlePagination
                      pageCount={pageCount}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SearchResult;
